var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { forwardRef } from 'react';
import classnames from 'classnames';
import { Popover } from '../Popover';
import { ButtonGroup } from '../ButtonGroup';
import { TagGroup } from '../TagGroup';
import { Button } from '../Button';
import { Icon } from '../Icon';
import { Tag } from '../Tag';
import { keys } from '../../utilities/keyCodes';
const defaultDecoration = { icon: true, underline: true, color: true };
export const InlineEdit = forwardRef((_a, ref) => {
    var { control: Control, decoration = defaultDecoration, onSave, onCancel, onChange, onClick, error: errorState = false, actionDirection = 'rb', actionLayout = 'inline', className, style, mode = 'read', value, width, showContentOnEdit, content: Content, autoFocusControl = true, id, placeholder = 'Enter value', portalDataAttributes } = _a, props = __rest(_a, ["control", "decoration", "onSave", "onCancel", "onChange", "onClick", "error", "actionDirection", "actionLayout", "className", "style", "mode", "value", "width", "showContentOnEdit", "content", "autoFocusControl", "id", "placeholder", "portalDataAttributes"]);
    const normalizeValue = (value) => typeof value === 'string' || typeof value === 'number'
        ? String(value)
        : value;
    const [newValue, setNewValue] = React.useState(normalizeValue(value));
    const [editing, setEditing] = React.useState(mode === 'edit');
    const [fontsize, setFontsize] = React.useState(16);
    const [isClosed, setIsClosed] = React.useState(false);
    const controlRef = React.useRef(null);
    const saveRef = React.useRef(null);
    const cancelRef = React.useRef(null);
    const textRef = React.useRef(null);
    const getDecoration = () => (Object.assign(Object.assign({}, defaultDecoration), decoration));
    React.useEffect(() => {
        setNewValue(normalizeValue(value));
    }, [value]);
    React.useEffect(() => {
        if (textRef.current) {
            setFontsize(Number(window
                .getComputedStyle(textRef.current)
                .getPropertyValue('font-size')
                .replace('px', '')));
        }
    }, []);
    const handleOpen = () => {
        onClick === null || onClick === void 0 ? void 0 : onClick();
        setEditing(true);
        setIsClosed(false);
    };
    const handleClose = () => {
        setEditing((prevState) => {
            prevState === true && setIsClosed(true);
            return false;
        });
    };
    React.useEffect(() => {
        var _a;
        isClosed && ((_a = textRef === null || textRef === void 0 ? void 0 : textRef.current) === null || _a === void 0 ? void 0 : _a.focus());
    }, [isClosed]);
    React.useEffect(() => {
        if (mode === 'edit')
            setEditing(true);
        if (mode === 'read') {
            setEditing(false);
            setNewValue(normalizeValue(value));
        }
    }, [mode, value]);
    const InlineEditKeyDown = (e) => {
        const isSpacePressed = e.key === keys.space;
        const isEnterPressed = e.key === keys.enter;
        if (isSpacePressed || isEnterPressed) {
            handleOpen();
            e.preventDefault();
        }
    };
    const handleCancel = () => {
        onCancel === null || onCancel === void 0 ? void 0 : onCancel();
        setNewValue(normalizeValue(value));
        handleClose();
    };
    const handleSave = () => {
        if (errorState)
            return; // if error is true, do not save
        onSave === null || onSave === void 0 ? void 0 : onSave(Object.assign(Object.assign({}, controlProps), { value: newValue }));
        handleClose();
    };
    const handleOnChange = (e, data) => {
        onChange === null || onChange === void 0 ? void 0 : onChange(e, data);
        if ((e === null || e === void 0 ? void 0 : e.target) instanceof HTMLInputElement ||
            (e === null || e === void 0 ? void 0 : e.target) instanceof HTMLTextAreaElement) {
            setNewValue(data.value);
        }
        else {
            setNewValue(e);
        }
    };
    const escapeOnKeyDown = (e) => {
        var _a;
        const isESCPressed = e.key === keys.esc;
        if (isESCPressed) {
            if (((_a = controlRef.current.state) === null || _a === void 0 ? void 0 : _a.openState) === true) {
                return e;
            }
            handleCancel();
            e.preventDefault();
        }
    };
    const keyOnCancel = (e) => {
        escapeOnKeyDown(e);
        const isTabPressed = e.key === keys.tab && actionLayout !== 'none';
        if (!isTabPressed)
            return;
        e.preventDefault();
        if (!e.shiftKey) {
            /* tab */
            controlRef.current.focus();
        }
        else {
            /* shift + tab */
            saveRef.current.focus();
        }
    };
    const keyOnControl = (e) => {
        escapeOnKeyDown(e);
        const isTabPressed = e.key === keys.tab && actionLayout !== 'none';
        if (!isTabPressed)
            return;
        e.preventDefault();
        if (!e.shiftKey) {
            /* tab */ saveRef.current.focus();
        }
        else {
            /* shift + tab */
            cancelRef.current.focus();
        }
    };
    const keyOnSave = (e) => {
        escapeOnKeyDown(e);
        const isTabPressed = e.key === keys.tab && actionLayout !== 'none';
        if (!isTabPressed)
            return;
        e.preventDefault();
        if (!e.shiftKey) {
            /* tab */ cancelRef.current.focus();
        }
        else {
            /* shift + tab */
            controlRef.current.focus();
        }
    };
    const left = ['l', 'lb', 'lt'];
    const bottom = ['b', 'br', 'bl'];
    const top = ['t', 'tr', 'tl'];
    const InlineEditClasses = classnames('InlineEdit', className, {
        'flex-column': bottom.includes(actionDirection) && actionLayout !== 'floating',
        'flex-column-reverse': top.includes(actionDirection) && actionLayout !== 'floating',
        'flex-row-reverse': left.includes(actionDirection) && actionLayout !== 'floating',
        'InlineEdit--fluid': width === 'fluid',
    });
    const ContentClasses = classnames('InlineEdit__content', {
        'InlineEdit__content--textcolor': !Content && getDecoration().color,
        'InlineEdit__content--underline': !Content && getDecoration().underline,
        'InlineEdit__content--icon': getDecoration().icon,
        'InlineEdit__content--error': errorState,
    });
    const ActionClasses = classnames(`InlineEdit__action`, {
        [`InlineEdit__action--${actionDirection}`]: actionLayout !== 'floating',
        'InlineEdit__action--floating': actionLayout === 'floating',
    });
    const EditIconClasses = classnames('InlineEdit__editIcon c-neutral-80 m-l-half', {
        'InlineEdit__editIcon--small': fontsize <= 16,
        'InlineEdit__editIcon--medium': fontsize <= 26 && fontsize > 16,
        'InlineEdit__editIcon--large': fontsize > 26,
    });
    const tagArray = (valueArr) => {
        return valueArr.map((singleValue, index) => {
            var _a;
            const tagText = singleValue.text
                ? singleValue.text
                : typeof singleValue.content === 'string'
                    ? singleValue.content
                    : String(singleValue.value);
            return (React.createElement(Tag, { key: index, title: (_a = singleValue.title) !== null && _a !== void 0 ? _a : tagText, color: singleValue.color }, tagText));
        });
    };
    const getContent = () => {
        var _a;
        const value = newValue;
        if (Content !== undefined) {
            return Content();
        }
        if (typeof value === 'string') {
            return value;
        }
        if (Array.isArray(value)) {
            if (value.length <= 3) {
                return React.createElement(TagGroup, null, tagArray(value.slice(0, 3)));
            }
            const truncated = value.slice(3);
            const title = truncated.reduce((acc, curr, i) => {
                return acc.concat(`${i === 0 ? '' : ', '}`, curr.text);
            }, '');
            const truncatedData = {
                text: `+${truncated.length}`,
                title,
            };
            const combinedArr = value.slice(0, 3).concat(truncatedData);
            return React.createElement(TagGroup, null, tagArray(combinedArr));
        }
        return String((_a = value === null || value === void 0 ? void 0 : value.text) !== null && _a !== void 0 ? _a : '');
    };
    const ValueClasses = classnames('InlineEdit__value', {
        'InlineEdit__value--placeholder': (newValue === undefined || newValue.length === 0) &&
            Content === undefined,
    });
    const readComponent = (React.createElement("span", Object.assign({ className: ContentClasses, ref: textRef, onClick: handleOpen, tabIndex: 0, onKeyDown: InlineEditKeyDown }, props),
        React.createElement("span", { className: ValueClasses }, (newValue === undefined || newValue.length === 0) &&
            Content === undefined
            ? placeholder
            : getContent()),
        React.createElement(Icon, { name: "edit", className: EditIconClasses })));
    const controlProps = {
        id,
        className: `InlineEdit__control`,
        ref: controlRef,
        error: errorState,
        autoFocus: autoFocusControl,
        onKeyDown: keyOnControl,
        onChange: handleOnChange,
        placeholder,
        trigger: { placeholder },
        value: newValue,
    };
    const internalCompProps = {
        save: handleSave,
        cancel: handleCancel,
        open: editing,
    };
    const actions = (React.createElement(ButtonGroup, { attached: true, className: ActionClasses },
        React.createElement(Button, { "aria-label": "save changes", small: true, ref: saveRef, className: "InlineEdit__save", primary: true, disabled: errorState, onClick: handleSave, onKeyDown: keyOnSave, type: "submit", iconName: "done" }),
        React.createElement(Button, { "aria-label": "discard changes", small: true, ref: cancelRef, className: "InlineEdit__cancel", type: "reset", iconName: "clear", onClick: handleCancel, onKeyDown: keyOnCancel })));
    const updateStyle = Object.assign({ maxWidth: width !== undefined && typeof width === 'string'
            ? `${width}`
            : width }, style);
    return (React.createElement("span", { className: InlineEditClasses, style: updateStyle, ref: ref, "data-anvil-component": "InlineEdit" }, editing ? (actionLayout === 'floating' ? (React.createElement("span", Object.assign({}, props),
        React.createElement(Popover, { trigger: Control(controlProps, internalCompProps), portal: true, direction: actionDirection, contentNoPadding: true, padding: null, width: "auto", className: "InlineEdit__floating__body", open: editing, offset: {
                distance: 8,
            }, el: width === 'fluid' ? 'div' : 'span', portalDataAttributes: portalDataAttributes }, actions))) : (React.createElement(React.Fragment, null,
        showContentOnEdit && readComponent,
        Control(controlProps, internalCompProps),
        actionLayout !== 'none' && actions))) : (readComponent)));
});
//# sourceMappingURL=InlineEdit.js.map